/**
 * KEET-559 — Bundle PDP option sync.
 *
 * A native Shopify bundle exposes one option per component, so a three-component
 * bundle renders three independent size selectors and Shopify generates the full
 * cartesian product of their values. For the Keetsa Dream Signature Suite that is
 * 27 variants, of which 3 are size-matched. The other 24 are purchasable and ship
 * mismatched components at prices that do not correspond to what was picked.
 *
 * This module makes the driver option (the anchor product — the mattress on a
 * mattress bundle, the pillow on a pillow bundle) drive the others. The slaved
 * selectors are hidden server-side in Liquid, not here: `product-info.js`
 * re-renders the whole section over the network on every option change, so any
 * attribute set from JS would be wiped on the next update.
 *
 * Ordering matters. `VariantSelects` (assets/global.js) listens for `change` on
 * itself in the bubble phase and publishes `optionValueSelectionChange`, whose
 * payload `selectedOptionValues` is read from `select option[selected]` — the
 * ATTRIBUTE, not the property. `product-info.js` then fetches a section render
 * keyed on those option value ids. So this module listens on `document` in the
 * CAPTURE phase, which runs before any of that, and moves both the property and
 * the attribute. By the time Dawn reads the DOM the selection is already matched,
 * and exactly one network round-trip happens rather than three.
 *
 * Listening on `document` rather than on `<variant-selects>` is deliberate: the
 * section render replaces that element, so a listener bound to it would be lost
 * after the first change.
 *
 * INITIAL SYNC (added after live testing, 2026-09-09). The first design assumed
 * Shopify's `selected_or_first_available_variant` would be the matched
 * combination, so nothing ran until the customer touched the selector. That is
 * false whenever any component option value is unavailable: Shopify skips it and
 * lands on the first AVAILABLE combination, which can be mismatched. Observed
 * live — the page loaded on `Queen / Queen / CalKing-14"H` because both the Queen
 * and King protector values were unavailable on the storefront. So the sync now
 * also runs once on load, and only dispatches a `change` (and therefore only
 * costs a request) when it actually had to move something.
 *
 * LIVE REGION (same date). The status element lives inside the section, so the
 * section render wipes the announcement a few hundred milliseconds after it is
 * written — before a screen reader can reliably read it. The message is therefore
 * held in memory and re-applied after `variantChange`, which fires once the
 * re-rendered DOM is in place.
 *
 * Depth (decision 2.1): the merchandiser deselects wrong-depth component variants
 * when building the bundle, so each slaved option should carry exactly one value
 * per size token and no depth logic is needed. Depth is used only to break a tie
 * when that rule was not followed, and only when the bundle parent carries
 * `keetsa.mattress_height_in`. An ambiguous matrix logs a warning so the mistake
 * surfaces in QA rather than shipping silently.
 *
 * Requires: assets/keetsa-size-match.js (window.KeetsaSizeMatch).
 */
(function () {
  'use strict';

  var CONTAINER_SELECTOR = '[data-bundle-option-sync]';
  var LOG_PREFIX = '[KEET-559]';

  // PUB_SUB_EVENTS is a top-level `const` in a classic script, so it is not a
  // window property and its presence depends on script order. Fall back to the
  // literal rather than assume constants.js has run.
  var VARIANT_CHANGE =
    (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) || 'variant-change';

  var pendingAnnouncement = {}; // sectionId -> text, held until the render settles
  var announceTimers = {};      // sectionId -> fallback timer handle

  // How long to wait for the section render before announcing anyway. Only reached
  // when the render never arrives (failed fetch); the normal path flushes on
  // `variantChange` well inside this.
  var ANNOUNCE_FALLBACK_MS = 900;

  function containerForSection(sectionId) {
    if (!sectionId) return null;
    var containers = document.querySelectorAll(CONTAINER_SELECTOR);
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].dataset.section === sectionId) return containers[i];
    }
    return null;
  }

  function driverSelectForSection(sectionId) {
    var pickers = document.querySelectorAll('variant-selects');
    for (var i = 0; i < pickers.length; i++) {
      if (pickers[i].dataset.section !== sectionId) continue;
      return pickers[i].querySelector('.product-form__input[data-bundle-driver] select');
    }
    return null;
  }

  /**
   * Every option value that normalizes to `token`. Values are read verbatim from
   * the DOM and never reconstructed — the live data mixes straight and curly
   * quotes between sizes (`60"W` vs `76"W`), so a rebuilt string will not match.
   */
  function candidatesForToken(select, token, KSM) {
    var out = [];
    for (var i = 0; i < select.options.length; i++) {
      var value = select.options[i].value;
      if (KSM.normalizeSizeName(value) === token) out.push(value);
    }
    return out;
  }

  function pickValue(select, token, requiredDepthIn, KSM) {
    var candidates = candidatesForToken(select, token, KSM);
    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];

    // Ambiguous by size token alone — decision 2.1A was not applied to this bundle.
    if (requiredDepthIn !== null && requiredDepthIn !== undefined) {
      for (var i = 0; i < candidates.length; i++) {
        if (KSM.extractDepthInches(candidates[i]) === requiredDepthIn) return candidates[i];
      }
    }
    console.warn(
      LOG_PREFIX + ' ambiguous bundle matrix: option "' + (select.name || select.id) + '" has ' +
      candidates.length + ' values for size "' + token + '" (' + candidates.join(' | ') + '). ' +
      'Deselect the wrong-depth component variants on the bundle. Falling back to the first.'
    );
    return candidates[0];
  }

  /**
   * Move both the `selected` property and the `selected` ATTRIBUTE. VariantSelects
   * reads the attribute (`select option[selected]`) to build the payload that drives
   * the section render, so setting `select.value` alone would update the visible
   * control and still fetch the previous variant.
   */
  function applyValue(select, value) {
    var options = select.options;
    var target = null;
    var i;

    for (i = 0; i < options.length; i++) {
      if (options[i].value === value) {
        target = options[i];
        break;
      }
    }
    if (!target) return false;
    if (target.hasAttribute('selected') && select.value === value) return false;

    for (i = 0; i < options.length; i++) {
      if (options[i].hasAttribute('selected')) options[i].removeAttribute('selected');
    }
    target.selected = true;
    target.setAttribute('selected', 'selected');
    return true;
  }

  function writeAnnouncement(sectionId) {
    var container = containerForSection(sectionId);
    if (!container) return;
    var status = container.querySelector('[data-bundle-sync-status]');
    if (!status) return;
    var text = pendingAnnouncement[sectionId] || '';
    if (status.textContent !== text) status.textContent = text;
  }

  /**
   * Only ever adds a message; never clears one. A sync that moves nothing has
   * nothing to say, and whatever was last announced is still true.
   *
   * The initial sync does not announce at all — see initAll. The customer has not
   * chosen anything yet, so there is nothing to confirm, and a message landing
   * during page load is either unsolicited noise or silently dropped depending on
   * when the screen reader registered the region. Neither is worth having; the
   * corrected state is carried by the selectors themselves.
   */
  function announce(sectionId, driverValue, changed) {
    if (!changed.length) return;
    pendingAnnouncement[sectionId] = driverValue + ' selected. Matched: ' + changed.join(', ') + '.';

    // Deliberately NOT written here. Writing on change announced it once from the
    // status element that is about to be destroyed, and once more from its
    // replacement — NVDA read the whole sentence twice per size change (confirmed
    // 2026-09-11 via Speech Viewer). Worse, which of the two a screen reader caught
    // depended on how fast the section render came back. Held instead, and flushed
    // once the new DOM is in place, so it is announced exactly once and after the
    // price and button have updated.
    if (announceTimers[sectionId]) clearTimeout(announceTimers[sectionId]);
    announceTimers[sectionId] = setTimeout(function () {
      announceTimers[sectionId] = null;
      flushAnnouncement(sectionId);
    }, ANNOUNCE_FALLBACK_MS);
  }

  /** Write the held message, once, and stop holding it. */
  function flushAnnouncement(sectionId) {
    if (announceTimers[sectionId]) {
      clearTimeout(announceTimers[sectionId]);
      announceTimers[sectionId] = null;
    }
    if (!pendingAnnouncement[sectionId]) return;
    writeAnnouncement(sectionId);
    pendingAnnouncement[sectionId] = null;
  }

  /**
   * Match every slaved option to the driver's size. Returns the values it moved.
   * Does not dispatch anything — callers decide whether Dawn needs telling.
   */
  function syncFrom(driverSelect, sectionId, options) {
    var KSM = window.KeetsaSizeMatch;
    if (!KSM) {
      console.error(LOG_PREFIX + ' keetsa-size-match.js unavailable — bundle option sync disabled');
      return [];
    }

    var container = containerForSection(sectionId);
    if (!container) return [];

    var driverValue = driverSelect.value;
    var token = KSM.normalizeSizeName(driverValue);
    if (!token) return [];

    var requiredDepthIn = KSM.requiredDepthFor(container.dataset.mattressHeightIn);
    var picker = driverSelect.closest('variant-selects');
    var slaved = picker.querySelectorAll('.product-form__input[data-bundle-slaved]');
    var changed = [];

    for (var i = 0; i < slaved.length; i++) {
      var slaveSelect = slaved[i].querySelector('select');
      if (!slaveSelect) {
        console.warn(
          LOG_PREFIX + ' a slaved bundle option is not a dropdown — sync skipped for it. ' +
          'Set the variant picker to "Dropdown" on the bundle template.'
        );
        continue;
      }

      var value = pickValue(slaveSelect, token, requiredDepthIn, KSM);
      if (value === null) {
        console.warn(
          LOG_PREFIX + ' no "' + driverValue + '" value on bundle option "' +
          (slaveSelect.name || slaveSelect.id) + '". The bundle offers a driver size its ' +
          'components do not — remove that size from the bundle.'
        );
        continue;
      }
      if (applyValue(slaveSelect, value)) changed.push(value);
    }

    if (!options || !options.silent) announce(sectionId, driverValue, changed);
    return changed;
  }

  function handleChange(event) {
    var select = event.target;
    if (!select || select.tagName !== 'SELECT') return;

    var wrapper = select.closest('.product-form__input');
    if (!wrapper || !wrapper.hasAttribute('data-bundle-driver')) return;

    var picker = select.closest('variant-selects');
    if (!picker) return;

    syncFrom(select, picker.dataset.section);
  }

  /**
   * Shopify can render a mismatched combination as the initial state whenever a
   * component option value is unavailable, because `selected_or_first_available_variant`
   * skips unavailable combinations. Correct it before the customer sees it. The
   * dispatch — and therefore the extra section render — happens only when something
   * actually moved, so a healthy bundle costs no additional request.
   */
  function initAll() {
    var containers = document.querySelectorAll(CONTAINER_SELECTOR);
    for (var i = 0; i < containers.length; i++) {
      var sectionId = containers[i].dataset.section;
      var driver = driverSelectForSection(sectionId);
      if (!driver) continue;

      if (syncFrom(driver, sectionId, { silent: true }).length) {
        console.warn(
          LOG_PREFIX + ' initial bundle state was mismatched and has been corrected. ' +
          'This happens when a component option value is unavailable, so Shopify\'s ' +
          'first-available variant is not the size-matched one.'
        );
        driver.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  // Capture phase on document: runs before VariantSelects' bubble-phase listener,
  // and survives the section re-render that replaces <variant-selects>.
  document.addEventListener('change', handleChange, true);

  // The announcement is held by announce() and written here, once the section render
  // has replaced the status element. Writing before the render meant announcing twice.
  if (typeof subscribe === 'function') {
    subscribe(VARIANT_CHANGE, function () {
      for (var key in pendingAnnouncement) {
        if (Object.prototype.hasOwnProperty.call(pendingAnnouncement, key)) flushAnnouncement(key);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
